import React, { useCallback, useRef } from 'react';
import useLiveTime from '@src/hooks/useLiveTime';
import useTimelineSeek from '@src/hooks/useTimelineSeek';
import { clamp } from '@src/services/timeline';
import {
  StyledFill,
  StyledProgressSlider,
  StyledThumb,
  StyledThumbAnchor,
} from './index.style';

/**
 * A whole-project progress bar with click / drag to seek — the audio player's
 * slider, for the timeline when it is COLLAPSED. Collapsing hides the ruler,
 * the tracks and the playhead, which is everything that shows where playback is
 * and everything that seeks; this strip gives both back in one 4px row.
 *
 * Expanded, the timeline itself does this job and the strip is not rendered.
 *
 * Like the playhead, the position is written straight to the DOM per animation
 * frame (useLiveTime) rather than held in state, so following playback
 * re-renders nothing. Seeking reuses the timeline's own gesture (useTimelineSeek
 * — throttled commits while dragging, one exact commit on release) with the rail
 * as its coordinate space, so scrubbing here behaves exactly as it does on the
 * tracks.
 *
 * @param sec         playback position (seconds); used while paused
 * @param playing     follow playback; off while scrubbing for a 1:1 drag
 * @param getSec      reads the live position without a re-render
 * @param durationSec full timeline length — the rail's right edge
 * @param onSeek      commit a new position (seconds)
 * @param disabled    no seeking (e.g. while the audio is being swapped)
 */
const TimelineProgressSlider = ({
  sec = 0,
  playing = false,
  getSec,
  durationSec = 0,
  onSeek,
  disabled = false,
}) => {
  const railRef = useRef(null);
  const fillRef = useRef(null);
  const thumbRef = useRef(null);

  const toSec = useCallback(
    (clientX) => {
      const el = railRef.current;
      if (!el || durationSec <= 0) return 0;
      const rect = el.getBoundingClientRect();
      if (!rect.width) return 0;
      const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
      return ratio * durationSec;
    },
    [durationSec],
  );

  const { onPointerDown, scrubSec } = useTimelineSeek({ onSeek, toSec });
  const isScrubbing = scrubSec != null;

  // Re-created when the duration changes, which re-places both parts against
  // the new scale.
  const draw = useCallback(
    (t) => {
      const ratio = durationSec > 0 ? clamp(t / durationSec, 0, 1) : 0;
      if (fillRef.current) {
        fillRef.current.style.transform = `scaleX(${ratio})`;
      }
      if (thumbRef.current) {
        thumbRef.current.style.transform = `translateX(${ratio * 100}%)`;
      }
    },
    [durationSec],
  );

  useLiveTime(
    isScrubbing ? scrubSec : sec,
    playing && !isScrubbing,
    draw,
    getSec,
  );

  return (
    <StyledProgressSlider
      ref={railRef}
      isDisabled={disabled}
      onPointerDown={disabled ? undefined : onPointerDown}
      role="slider"
      aria-label="playback position"
      aria-valuemin={0}
      aria-valuemax={durationSec}
      aria-valuenow={isScrubbing ? scrubSec : sec}
    >
      <StyledFill ref={fillRef} />
      <StyledThumbAnchor ref={thumbRef}>
        <StyledThumb />
      </StyledThumbAnchor>
    </StyledProgressSlider>
  );
};

export default TimelineProgressSlider;
